"use client";

import { useState } from "react";
import Image from "next/image";
import { Globe, Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface HeadingNode {
  level: number;
  text: string;
  order: number;
}

interface HeadingResult {
  url: string;
  fetchedAt: string;
  headings: HeadingNode[];
  warnings: string[];
}


const normalizeInputUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ensured = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const url = new URL(ensured);
    if (url.protocol === "http:") url.protocol = "https:";
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
};

const faviconFromUrl = (value: string): string | null => {
  const normalized = normalizeInputUrl(value);
  if (!normalized) return null;
  try {
    const url = new URL(normalized);
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
  } catch {
    return null;
  }
};

export function HeadingStructureTool() {
  const [url, setUrl] = useState("");
  const [favicon, setFavicon] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<HeadingResult | null>(null);

  const updateFavicon = (value: string) => {
    const next = faviconFromUrl(value);
    setFavicon(next);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!url.trim()) {
      setError("Please enter a valid URL.");
      return;
    }

    const normalized = normalizeInputUrl(url);
    if (!normalized) {
      setError("Please enter a valid URL.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/tools/headings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: normalized }),
      });
      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.error ?? "Unable to fetch headings.");
      }

      setResult(json.data as HeadingResult);
    } catch (err) {
      setResult(null);
      setError(
        err instanceof Error ? err.message : "Something went wrong. Try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-2xl flex-col gap-3 sm:mx-auto sm:flex-row sm:items-center sm:gap-4"
      >
        <div className="relative flex-1">
          <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2">
            {favicon ? (
              <Image
                src={favicon}
                alt=""
                width={20}
                height={20}
                unoptimized
                className="h-5 w-5 sm:h-6 sm:w-6 rounded-sm"
              />
            ) : (
              <Globe className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
            )}
          </div>
          <span className="pointer-events-none absolute left-12 sm:left-14 top-1/2 h-6 sm:h-8 -translate-y-1/2 border-l border-border" />
          <Input
            type="text"
            placeholder="website.com"
            className="h-10 w-full pl-14 text-sm sm:h-12 sm:pl-16 sm:text-base border-2 border-border/20 bg-transparent focus:border-primary/50"
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
              updateFavicon(event.target.value);
              setError(null);
            }}
            autoComplete="url"
            aria-label="Website URL"
          />
        </div>
        <Button
          type="submit"
          size="lg"
          disabled={isLoading}
          className="shrink-0 h-10 w-full sm:h-12 sm:w-auto"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading
            </>
          ) : (
            "Inspect"
          )}
        </Button>
      </form>
      {error && (
        <p className="text-center text-sm font-medium text-destructive">
          {error}
        </p>
      )}

      {result ? (
        <div className="space-y-6">
          <Card>
            <CardHeader className="gap-1">
              <CardTitle>Heading outline</CardTitle>
              <CardDescription>
                {result.url} · fetched{" "}
                {new Date(result.fetchedAt).toLocaleString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {result.headings.length ? (
                <div className="space-y-1">
                  {result.headings.map((heading) => {
                    const indentLevel = heading.level - 1;
                    const indentBase = 16; // Base indent in pixels
                    const indent = indentLevel * indentBase;

                    return (
                      <div
                        key={`${heading.level}-${heading.order}`}
                        className="flex items-start gap-3 py-2.5"
                        style={{ paddingLeft: `${indent}px` }}
                      >
                        {/* Level badge with H1, H2, etc. */}
                        <div className="flex-shrink-0">
                          <span
                            className={cn(
                              "inline-flex items-center justify-center rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide",
                              heading.level === 1
                                ? "bg-primary/10 text-primary"
                                : heading.level === 2
                                  ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
                                  : heading.level === 3
                                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                    : heading.level === 4
                                      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                                      : heading.level === 5
                                        ? "bg-purple-500/10 text-purple-600 dark:text-purple-400"
                                        : "bg-rose-500/10 text-rose-600 dark:text-rose-400"
                            )}
                          >
                            H{heading.level}
                          </span>
                        </div>

                        {/* Heading content */}
                        <div className="flex-1 min-w-0">
                          <p
                            className={cn(
                              "font-medium leading-relaxed text-foreground",
                              heading.level === 1
                                ? "text-base sm:text-lg"
                                : heading.level === 2
                                  ? "text-sm sm:text-base"
                                  : "text-xs sm:text-sm"
                            )}
                          >
                            {heading.text || "Untitled heading"}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No headings detected on this page.
                </p>
              )}
            </CardContent>
          </Card>

          {result.warnings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Issues detected</CardTitle>
                <CardDescription>
                  Fix these heading problems to improve SEO and accessibility.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-rose-500">
                  {result.warnings.map((warning) => (
                    <li
                      key={warning}
                      className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3"
                    >
                      {warning}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <div className="mt-12 rounded-2xl border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground">
          Run your first inspection to visualize the exact H1–H6 outline for any URL.
        </div>
      )}
    </div>
  );
}
